/**
 * Google Analytics (GA4) utility for Brakomp apps
 *
 * Provides consent-based tracking with:
 * - Reactive consent state
 * - Automatic pageview tracking
 * - Custom event tracking
 * - Privacy-compliant by default
 */

let Vue = null

// Reactive consent state - shared across all components
const gaConsent = { analytics_storage: false }

// Track secondary GA IDs to prevent duplicates
const secondaryGaIds = []

/**
 * Load Google Analytics gtag.js script
 * @param {string} gaId - GA4 measurement ID (e.g., G-XXXXXXXXXX)
 */
function loadGtagScript(gaId) {
  // Create and inject gtag.js script
  const script = document.createElement("script")
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`
  document.head.appendChild(script)

  // Initialize dataLayer and gtag function
  window.dataLayer = window.dataLayer || []
  window.gtag = function() {
    window.dataLayer.push(arguments)
  }

  // Initialize gtag with timestamp
  window.gtag("js", new Date())

  // Set initial consent to denied
  window.gtag("consent", "default", {
    ad_user_data: "denied",
    ad_personalization: "denied",
    ad_storage: "denied",
    analytics_storage: "denied",
    wait_for_update: 500,
  })

  // Configure with measurement ID and default consent state (denied)
  window.gtag("config", gaId, {
    send_page_view: false, // We'll handle pageviews manually
  })
}

/**
 * Initialize Google Analytics
 * @param {Object} VueInstance - Vue constructor
 * @param {string} gaId - GA4 measurement ID
 */
export function initializeAnalytics(VueInstance, gaId) {
  if (!gaId || typeof gaId !== "string" || !gaId.trim()) {
    console.info("[Analytics] No GA ID provided, tracking disabled")
    return
  }

  Vue = VueInstance

  // Make consent state reactive
  if (Vue.observable) {
    Object.assign(gaConsent, Vue.observable(gaConsent))
  }

  try {
    loadGtagScript(gaId)

    // Store GA ID for later use
    window._gaId = gaId

    console.info("[Analytics] Initialized with ID:", gaId)
  } catch (error) {
    console.error("[Analytics] Failed to initialize:", error)
  }
}

/**
 * Initialize a secondary Google Analytics tracker (e.g., customer GA)
 * @param {string} gaId - GA4 measurement ID
 * @returns {boolean} Success status
 */
export function initializeSecondaryAnalytics(gaId) {
  if (!gaId || typeof gaId !== "string" || !gaId.trim()) {
    return false
  }

  if (!window.gtag) {
    console.warn("[Analytics] Primary GA not initialized")
    return false
  }

  // Avoid duplicates
  if (secondaryGaIds.includes(gaId) || window._gaId === gaId) {
    return true
  }

  try {
    window.gtag("config", gaId, { send_page_view: false })
    secondaryGaIds.push(gaId)
    console.info("[Analytics] Secondary GA initialized:", gaId)

    // Re-apply current consent state so the new property picks it up
    if (gaConsent.analytics_storage) {
      window.gtag("consent", "update", {
        analytics_storage: "granted",
      })
      console.info("[Analytics] Consent granted (re-applied for secondary)")
    }
    return true
  } catch (error) {
    console.error("[Analytics] Failed to initialize secondary:", error)
    return false
  }
}

/**
 * Set analytics consent state
 * @param {boolean} hasConsent - Whether user has granted consent
 */
export function setAnalyticsConsent(hasConsent) {
  gaConsent.analytics_storage = !!hasConsent

  if (window.gtag) {
    window.gtag("consent", "update", {
      analytics_storage: hasConsent ? "granted" : "denied",
    })

    console.info("[Analytics] Consent", hasConsent ? "granted" : "denied")
  }
}

/**
 * Track a pageview
 * @param {string} path - Page path (e.g., /courses)
 * @param {string} title - Page title
 */
export function trackPageView(path, title) {
  if (!gaConsent.analytics_storage || !window.gtag) {
    return
  }

  try {
    const trackerIds = [window._gaId, ...secondaryGaIds].filter(Boolean)

    if (trackerIds.length === 0) return

    trackerIds.forEach(gaId => {
      window.gtag("config", gaId, {
        page_path: path,
        page_title: title,
      })
    })

    console.debug(
      "[Analytics] Pageview to",
      trackerIds.length,
      "tracker(s):",
      path,
    )
  } catch (error) {
    console.error("[Analytics] Failed to track pageview:", error)
  }
}

/**
 * Track a custom event
 * @param {string} eventName - Event name (e.g., 'button_click')
 * @param {Object} params - Event parameters
 */
export function trackEvent(eventName, params = {}) {
  if (!gaConsent.analytics_storage || !window.gtag) {
    return
  }

  try {
    const trackerIds = [window._gaId, ...secondaryGaIds].filter(Boolean)

    if (trackerIds.length > 1) {
      params.send_to = trackerIds
    }

    window.gtag("event", eventName, params)

    console.debug(
      "[Analytics] Event to",
      trackerIds.length,
      "tracker(s):",
      eventName,
    )
  } catch (error) {
    console.error("[Analytics] Failed to track event:", error)
  }
}

/**
 * Get current consent state
 * @returns {boolean} Whether consent is granted
 */
export function getAnalyticsConsent() {
  return gaConsent.analytics_storage
}
